import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { User, Edit, Save, X, Lock, Key, Mail, User as UserIcon, Calendar, RotateCcw, RefreshCw, Unlock, Shield, Copy, CheckCircle2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import './Profile.css';

interface UserProfile {
  id: string;
  email: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  lastLogin: string | null;
  createdAt: string;
}

const Profile: React.FC = () => {
  const { user: authUser, setUser, logout } = useAuth();
  const [editingProfile, setEditingProfile] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [profileData, setProfileData] = useState({
    email: '',
    username: '',
    firstName: '',
    lastName: '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showMfaSection, setShowMfaSection] = useState(false);
  const [mfaStatus, setMfaStatus] = useState<any>(null);
  const [showMfaReset, setShowMfaReset] = useState(false);
  const [mfaResetPassword, setMfaResetPassword] = useState('');
  const [mfaResetData, setMfaResetData] = useState<any>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  // Fetch user profile data
  const { data: profileResponse, isLoading, refetch } = useQuery(
    'userProfile',
    async () => {
      const response = await api.get('/auth/me');
      return response.data;
    },
    {
      onSuccess: (data) => {
        const user = data.user;
        setProfileData({
          email: user.email || '',
          username: user.username || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
        });
      },
    }
  );

  // Fetch MFA status
  const { data: mfaStatusResponse, refetch: refetchMfaStatus } = useQuery(
    'mfaStatus',
    async () => {
      const response = await api.get('/auth/me/mfa');
      return response.data;
    },
    {
      enabled: showMfaSection,
      onSuccess: (data) => {
        setMfaStatus(data);
      },
    }
  );

  // MFA Reset Mutation
  const resetMFAMutation = useMutation(
    async (password: string) => {
      const response = await api.post('/auth/me/mfa/reset', { password });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('MFA reset successfully. Please scan the QR code with your authenticator app.');
        setMfaResetData(data);
        setBackupCodes(data.backupCodes || []);
        setShowBackupCodes(true);
        setMfaResetPassword('');
        refetchMfaStatus();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to reset MFA');
      },
    }
  );

  // Regenerate Backup Codes Mutation
  const regenerateBackupCodesMutation = useMutation(
    async (password: string) => {
      const response = await api.post('/auth/me/mfa/regenerate-backup-codes', { password });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Backup codes regenerated successfully');
        setBackupCodes(data.backupCodes || []);
        setShowBackupCodes(true);
        refetchMfaStatus();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to regenerate backup codes');
      },
    }
  );

  // Disable MFA Mutation
  const disableMFAMutation = useMutation(
    async (password: string) => {
      const response = await api.post('/auth/me/mfa/disable', { password });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message || 'MFA disabled successfully. You will be logged out.');
        refetchMfaStatus();
        setTimeout(() => {
          logout();
          window.location.href = '/login';
        }, 2000);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to disable MFA');
      },
    }
  );

  const handleResetMFA = () => {
    if (!mfaResetPassword) {
      toast.error('Please enter your password');
      return;
    }
    resetMFAMutation.mutate(mfaResetPassword);
  };

  const handleRegenerateBackupCodes = () => {
    const password = prompt('Enter your password to regenerate backup codes:');
    if (password) {
      regenerateBackupCodesMutation.mutate(password);
    }
  };

  const handleDisableMFA = () => {
    if (window.confirm('Are you sure you want to disable MFA? This will log you out of all devices.')) {
      const password = prompt('Enter your password to disable MFA:');
      if (password) {
        disableMFAMutation.mutate(password);
      }
    }
  };

  const user: UserProfile | null = profileResponse?.user || null;

  // Update profile mutation
  const updateProfileMutation = useMutation(
    async (data: any) => {
      const response = await api.put('/auth/me/profile', data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Profile updated successfully');
        setEditingProfile(false);
        // Update auth context with new user data
        if (data.user) {
          setUser({ ...authUser, ...data.user } as any);
          localStorage.setItem('user', JSON.stringify({ ...authUser, ...data.user }));
        }
        refetch();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to update profile');
      },
    }
  );

  // Update password mutation
  const updatePasswordMutation = useMutation(
    async (data: { currentPassword: string; newPassword: string }) => {
      const response = await api.put('/auth/me/password', data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message || 'Password updated successfully. Please login again.');
        setShowPasswordForm(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        // Logout user after password change (sessions are revoked)
        setTimeout(() => {
          logout();
          window.location.href = '/login';
        }, 2000);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to update password');
      },
    }
  );

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate(profileData);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    // Validate password strength
    if (passwordData.newPassword.length < 12) {
      toast.error('Password must be at least 12 characters long');
      return;
    }

    updatePasswordMutation.mutate({
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
    });
  };

  if (isLoading) {
    return (
      <div className="profile-container">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-container">
        <div className="error">Failed to load profile</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="profile-avatar">
          <UserIcon size={48} />
        </div>
        <div className="profile-title">
          <h1>My Profile</h1>
          <p className="profile-subtitle">Manage your account settings and password</p>
        </div>
      </div>

      <div className="profile-content">
        {/* Profile Information Section */}
        <div className="profile-section">
          <div className="section-header">
            <h2>
              <User size={20} />
              Profile Information
            </h2>
            {!editingProfile && (
              <button
                className="btn-edit"
                onClick={() => setEditingProfile(true)}
              >
                <Edit size={16} />
                Edit Profile
              </button>
            )}
          </div>

          {editingProfile ? (
            <form onSubmit={handleProfileSubmit} className="profile-form">
              <div className="form-group">
                <label>
                  <Mail size={16} />
                  Email
                </label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  <UserIcon size={16} />
                  Username
                </label>
                <input
                  type="text"
                  value={profileData.username}
                  onChange={(e) => setProfileData({ ...profileData, username: e.target.value })}
                  required
                  minLength={3}
                  maxLength={50}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setEditingProfile(false);
                    // Reset form data
                    setProfileData({
                      email: user.email || '',
                      username: user.username || '',
                      firstName: user.firstName || '',
                      lastName: user.lastName || '',
                    });
                  }}
                >
                  <X size={16} />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={updateProfileMutation.isLoading}
                >
                  <Save size={16} />
                  {updateProfileMutation.isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          ) : (
            <div className="profile-info">
              <div className="info-item">
                <div className="info-label">
                  <Mail size={16} />
                  Email
                </div>
                <div className="info-value">{user.email}</div>
              </div>

              <div className="info-item">
                <div className="info-label">
                  <UserIcon size={16} />
                  Username
                </div>
                <div className="info-value">{user.username}</div>
              </div>

              {user.firstName && (
                <div className="info-item">
                  <div className="info-label">First Name</div>
                  <div className="info-value">{user.firstName}</div>
                </div>
              )}

              {user.lastName && (
                <div className="info-item">
                  <div className="info-label">Last Name</div>
                  <div className="info-value">{user.lastName}</div>
                </div>
              )}

              <div className="info-item">
                <div className="info-label">Role</div>
                <div className="info-value role-badge">{user.role.replace('_', ' ')}</div>
              </div>

              {user.lastLogin && (
                <div className="info-item">
                  <div className="info-label">
                    <Calendar size={16} />
                    Last Login
                  </div>
                  <div className="info-value">
                    {new Date(user.lastLogin).toLocaleString()}
                  </div>
                </div>
              )}

              <div className="info-item">
                <div className="info-label">
                  <Calendar size={16} />
                  Member Since
                </div>
                <div className="info-value">
                  {new Date(user.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Password Update Section */}
        <div className="profile-section">
          <div className="section-header">
            <h2>
              <Lock size={20} />
              Password Security
            </h2>
            {!showPasswordForm && (
              <button
                className="btn-edit"
                onClick={() => setShowPasswordForm(true)}
              >
                <Key size={16} />
                Change Password
              </button>
            )}
          </div>

          {showPasswordForm ? (
            <form onSubmit={handlePasswordSubmit} className="profile-form">
              <div className="form-group">
                <label>
                  <Lock size={16} />
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  required
                  placeholder="Enter your current password"
                />
              </div>

              <div className="form-group">
                <label>
                  <Key size={16} />
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  required
                  minLength={12}
                  placeholder="At least 12 characters with uppercase, lowercase, number, and special character"
                />
                <div className="password-hint">
                  Password must be at least 12 characters and include uppercase, lowercase, number, and special character
                </div>
              </div>

              <div className="form-group">
                <label>
                  <Key size={16} />
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                  minLength={12}
                  placeholder="Confirm your new password"
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: '',
                    });
                  }}
                >
                  <X size={16} />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={updatePasswordMutation.isLoading}
                >
                  <Save size={16} />
                  {updatePasswordMutation.isLoading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          ) : (
            <div className="password-info">
              <p>Your password was last updated when you created your account.</p>
              <p className="password-warning">
                <Lock size={16} />
                For security, changing your password will log you out of all devices and require you to login again.
              </p>
            </div>
          )}
        </div>

        {/* MFA Management Section */}
        <div className="profile-section">
          <div className="section-header">
            <h2>
              <Shield size={20} />
              Multi-Factor Authentication (MFA)
            </h2>
            <button
              className="btn-edit"
              onClick={() => {
                setShowMfaSection(!showMfaSection);
                if (!showMfaSection) {
                  refetchMfaStatus();
                }
              }}
            >
              {showMfaSection ? <X size={16} /> : <Shield size={16} />}
              {showMfaSection ? 'Hide' : 'Manage MFA'}
            </button>
          </div>

          {showMfaSection && (
            <div className="mfa-management">
              {mfaStatus && (
                <>
                  <div className="mfa-status-display">
                    <div className="info-item">
                      <div className="info-label">MFA Status:</div>
                      <div className="info-value">
                        {mfaStatus.mfaEnabled ? (
                          <span className="mfa-badge enabled">
                            <CheckCircle2 size={16} />
                            <span>Enabled</span>
                          </span>
                        ) : (
                          <span className="mfa-badge disabled">
                            <AlertCircle size={16} />
                            <span>Disabled</span>
                          </span>
                        )}
                      </div>
                    </div>
                    {mfaStatus.mfaEnabled && (
                      <div className="info-item">
                        <div className="info-label">Backup Codes Available:</div>
                        <div className="info-value">{mfaStatus.backupCodesCount} codes</div>
                      </div>
                    )}
                  </div>

                  {mfaStatus.mfaEnabled ? (
                    <div className="mfa-actions">
                      <div className="action-info">
                        <p>
                          <strong>Changed your device?</strong> Reset MFA to generate a new QR code for your new authenticator app.
                        </p>
                        <p>
                          <strong>Lost your backup codes?</strong> Regenerate new backup codes (old codes will no longer work).
                        </p>
                      </div>

                      {!showMfaReset ? (
                        <div className="mfa-action-buttons">
                          <button
                            className="btn-secondary"
                            onClick={() => setShowMfaReset(true)}
                            disabled={resetMFAMutation.isLoading}
                          >
                            <RotateCcw size={16} />
                            <span>Reset MFA (New Device)</span>
                          </button>
                          <button
                            className="btn-secondary"
                            onClick={handleRegenerateBackupCodes}
                            disabled={regenerateBackupCodesMutation.isLoading}
                          >
                            <RefreshCw size={16} />
                            <span>Regenerate Backup Codes</span>
                          </button>
                          <button
                            className="btn-secondary danger"
                            onClick={handleDisableMFA}
                            disabled={disableMFAMutation.isLoading}
                          >
                            <Unlock size={16} />
                            <span>Disable MFA</span>
                          </button>
                        </div>
                      ) : (
                        <div className="mfa-reset-form">
                          <h3>Reset MFA</h3>
                          <p className="mfa-reset-info">
                            Enter your password to reset MFA. This will generate a new QR code that you can scan with your authenticator app.
                          </p>
                          <div className="form-group">
                            <label>
                              <Lock size={16} />
                              Password
                            </label>
                            <input
                              type="password"
                              value={mfaResetPassword}
                              onChange={(e) => setMfaResetPassword(e.target.value)}
                              placeholder="Enter your password"
                              required
                            />
                          </div>
                          <div className="form-actions">
                            <button
                              type="button"
                              className="btn-cancel"
                              onClick={() => {
                                setShowMfaReset(false);
                                setMfaResetPassword('');
                              }}
                            >
                              <X size={16} />
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn-save"
                              onClick={handleResetMFA}
                              disabled={resetMFAMutation.isLoading || !mfaResetPassword}
                            >
                              <RotateCcw size={16} />
                              {resetMFAMutation.isLoading ? 'Resetting...' : 'Reset MFA'}
                            </button>
                          </div>
                        </div>
                      )}

                      {mfaResetData && (
                        <div className="mfa-setup-result">
                          <h3>Scan QR Code</h3>
                          <p>Scan this QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, etc.)</p>
                          <div className="qr-code-container">
                            <img src={mfaResetData.qrCodeUrl} alt="MFA QR Code" />
                          </div>
                          <div className="manual-entry">
                            <p><strong>Or enter manually:</strong></p>
                            <code>{mfaResetData.manualEntryKey}</code>
                            <button
                              className="copy-btn"
                              onClick={() => {
                                navigator.clipboard.writeText(mfaResetData.manualEntryKey);
                                toast.success('Key copied to clipboard');
                              }}
                            >
                              <Copy size={14} />
                            </button>
                          </div>
                        </div>
                      )}

                      {showBackupCodes && backupCodes.length > 0 && (
                        <div className="backup-codes-display">
                          <h3>Backup Codes</h3>
                          <p className="backup-codes-warning">
                            ⚠️ Save these codes securely. They will not be shown again.
                          </p>
                          <div className="backup-codes-grid">
                            {backupCodes.map((code, index) => (
                              <div key={index} className="backup-code-item">
                                <code>{code}</code>
                                <button
                                  className="copy-code-btn"
                                  onClick={() => {
                                    navigator.clipboard.writeText(code);
                                    toast.success('Code copied');
                                  }}
                                >
                                  <Copy size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                          <button
                            className="btn-secondary"
                            onClick={() => {
                              const allCodes = backupCodes.join('\n');
                              navigator.clipboard.writeText(allCodes);
                              toast.success('All codes copied');
                            }}
                          >
                            <Copy size={16} />
                            <span>Copy All Codes</span>
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mfa-not-enabled">
                      <p>MFA is not enabled for your account. MFA setup is required during login.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;

